import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "viewSelect",
    "varLimit",
    "varRange"
  ]

  static values = {
    isNew: Boolean
  }

  initialize() {
    this.disableAllFields();
  }

  connect() {
    if (this.hasViewSelectTarget) {
      if (this.viewSelectTarget.value) {
        this.handleViewChange();
      }
    } else {
      const select = this.element.querySelector('select[name$="[attribution_view_id]"]');
      if (select) {
        select.addEventListener('change', this.handleViewChange.bind(this));
        if (select.value) {
          this.handleViewChange();
        }
      }
    }
  }

  /**
   * Handles changes in the attribution view selector.
   * Resets fields and updates their state based on the selected view.
   * @param {Event} [event] - Change event from the select element
   */
  handleViewChange(event) {
    const viewId = this.hasViewSelectTarget ?
      this.viewSelectTarget.value :
      this.element.querySelector('select[name$="[attribution_view_id]"]')?.value;

    if (this.isNewValue) {
      this.resetFields();
    }

    if (!viewId) {
      this.disableAllFields();
      return;
    }

    this.fetchViewTypeInfo(viewId);
  }

  /**
   * Fetches view type information from the server and updates field states accordingly.
   * @param {string} viewId - ID of the selected attribution view
   */
  async fetchViewTypeInfo(viewId) {
    try {
      const response = await fetch(`/risk/attribution_view_alerts/view_type_info?view_id=${viewId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.is_allocation) {
        this.enableAllFields();
      } else {
        this.handleAllocationView();
      }
    } catch (error) {
      this.disableAllFields();
    }
  }

  /**
   * Handles the specific case of an allocation view.
   * Enables all fields except those related to VAR calculations.
   * Sets VAR fields to '-' when disabled to prevent nil values.
   */
  handleAllocationView() {
    // First enable all fields
    this.enableAllFields();

    // Then disable VAR-related fields and set their values to '-'
    this.element.querySelectorAll('input:not([type="submit"]), select').forEach(field => {
      const isVarField = field.name && (
        field.name.endsWith('[var_limit]') ||
        field.name.endsWith('[var_range]') ||
        field.name.endsWith('[validate_var_limit]')
      );

      if (isVarField) {
        field.disabled = true;
        
        // Set value to empty for var_limit (will be handled by model as nil)
        if (field.name.endsWith('[var_limit]')) {
          field.value = '';
        } else if (field.name.endsWith('[var_range]')) {
          // For select fields, set to '-'
          if (field.tomselect) {
            field.tomselect.setValue('-', true);
            field.tomselect.disable();
          } else {
            field.value = '-';
          }
        }
        
        // Disable tomselect for validate_var_limit if it exists
        if (field.tomselect && !field.name.endsWith('[var_range]')) {
          field.tomselect.disable();
        }
      }
    });
  }

  /**
   * Resets all form fields to their default values.
   * Does not affect the attribution view selector or submit buttons.
   */
  resetFields() {
    if (!this.isNewValue) return;

    this.element.querySelectorAll('input:not([type="submit"]), select').forEach(field => {
      const isViewSelect = field.name && field.name.endsWith('[attribution_view_id]');
      if (!isViewSelect) {
        // Reset based on field type
        if (field.type === 'checkbox') {
          field.checked = false;
        } else if (field.tomselect) {
          field.tomselect.clear();
        } else {
          field.value = '';
        }
      }
    });
  }

  /**
   * Disables all form fields except the view selector and submit buttons.
   * Handles both regular fields and TomSelect instances.
   */
  disableAllFields() {
    this.element.querySelectorAll('input:not([type="submit"]), select').forEach(field => {
      const isViewSelect = field.name && field.name.endsWith('[attribution_view_id]');
      if (!isViewSelect) {
        field.disabled = true;
        if (field.tomselect) {
          field.tomselect.disable();
        }
      }
    });
  }

  /**
   * Enables all form fields except the view selector.
   * Handles both regular fields and TomSelect instances.
   */
  enableAllFields() {
    this.element.querySelectorAll('input:not([type="submit"]), select').forEach(field => {
      const isViewSelect = field.name && field.name.endsWith('[attribution_view_id]');
      if (!isViewSelect) {
        field.disabled = false;
        if (field.tomselect) {
          field.tomselect.enable();
        }
      }
    });
  }
}